import React, { useMemo, useState } from 'react';
import { usePhotoSrc } from '../../utils/use-photo-src';

type PhotoRecord = Record<string, any>;

interface HeroCandidateInput {
    selectedHeroImage?: unknown;
    quoteHeroImage?: unknown;
    photos?: unknown;
}

function matchingPhoto(value: unknown, photos: PhotoRecord[]): PhotoRecord | null {
    if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
        return photos[value] || null;
    }
    if (typeof value !== 'string') return null;
    const key = value.trim();
    if (!key) return null;
    return photos.find(photo => [
        photo?.id,
        photo?.photoId,
        photo?.photo_id,
        photo?.blobId,
        photo?.s3Key,
        photo?.s3_key,
        photo?.cloudObjectKey,
        photo?.cloud_object_key,
        photo?.fileMetadataId,
        photo?.file_metadata_id,
        photo?.name,
        photo?.originalName,
    ].some(candidate => candidate != null && String(candidate) === key)) || null;
}

function toRenderablePhotoRecord(record: PhotoRecord): PhotoRecord {
    const s3Key = [record.s3Key, record.s3_key, record.cloudObjectKey, record.cloud_object_key]
        .find(value => typeof value === 'string' && value.trim());
    const fileMetadataId = [record.fileMetadataId, record.file_metadata_id]
        .find(value => value !== null && value !== undefined && value !== '');
    const photoId = record.photoId ?? record.photo_id;
    const normalizedS3Key = s3Key ? String(s3Key).trim() : '';
    const needsNormalization = (photoId !== null && photoId !== undefined && photoId !== ''
            && record.photoId !== photoId)
        || (normalizedS3Key && record.s3Key !== normalizedS3Key)
        || (fileMetadataId !== null && fileMetadataId !== undefined
            && record.fileMetadataId !== fileMetadataId);
    let renderable = needsNormalization
        ? {
            ...record,
            ...(photoId !== null && photoId !== undefined && photoId !== '' ? { photoId } : {}),
            ...(normalizedS3Key ? { s3Key: normalizedS3Key } : {}),
            ...(fileMetadataId !== null && fileMetadataId !== undefined
                ? { fileMetadataId }
                : {}),
        }
        : record;
    const hasStableDisplayIdentity = [
        renderable.id,
        renderable.blobId,
        renderable.s3Key,
        renderable.fileMetadataId,
        renderable.previewUrl,
        renderable.signedUrl,
        renderable.url,
        renderable.thumbnailUrl,
        renderable.name,
    ].some(value => value !== null && value !== undefined && value !== '');
    const inline = [renderable.data, renderable.dataUrl]
        .find(value => typeof value === 'string' && /^data:image\//i.test(value));
    // usePhotoSrc intentionally binds an async resolution receipt to the
    // photo's identity. Inline-only legacy records otherwise fall back to
    // object identity and lose the receipt if a parent recreates the wrapper.
    if (!hasStableDisplayIdentity && inline) renderable = { ...renderable, url: inline };
    return renderable;
}

function hasResolvablePhotoSource(record: PhotoRecord): boolean {
    return [
        record.data,
        record.dataUrl,
        record.thumbnailUrl,
        record.previewUrl,
        record.signedUrl,
        record.url,
        record.blobId,
        record.s3Key,
        record.fileMetadataId,
    ].some(value => value !== null && value !== undefined && value !== '');
}

function hasPortablePhotoSource(record: PhotoRecord): boolean {
    return [
        record.data,
        record.dataUrl,
        record.thumbnailUrl,
        record.previewUrl,
        record.signedUrl,
        record.url,
        record.s3Key,
        record.fileMetadataId,
    ].some(value => value !== null && value !== undefined && value !== '');
}

/**
 * An expiring preview/signed URL is renderable, but it is not the identity of
 * the saved customer file. Keep candidate ordering aligned with usePhotoSrc:
 * durable cloud references are resolved before session URL fallbacks.
 */
function hasDurablePhotoIdentity(record: PhotoRecord): boolean {
    return [record.s3Key, record.fileMetadataId]
        .some(value => value !== null && value !== undefined && value !== '');
}

function mergeHeroCandidateWithPhoto(candidate: PhotoRecord, photo: PhotoRecord): PhotoRecord {
    const merged = { ...photo, ...candidate };
    const preserveFromPhotoWhenBlank = [
        'data',
        'dataUrl',
        'thumbnailUrl',
        'previewUrl',
        'signedUrl',
        'url',
        's3Key',
        's3_key',
        'cloudObjectKey',
        'cloud_object_key',
        'fileMetadataId',
        'file_metadata_id',
    ];
    for (const key of preserveFromPhotoWhenBlank) {
        if ((merged[key] === null || merged[key] === undefined || merged[key] === '')
            && photo[key] !== null && photo[key] !== undefined && photo[key] !== '') {
            merged[key] = photo[key];
        }
    }
    return merged;
}

function normalizeHeroCandidate(value: unknown, photos: PhotoRecord[]): PhotoRecord | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'object' && !Array.isArray(value)) {
        const record = value as PhotoRecord;
        const reference = record.id
            ?? record.photoId
            ?? record.photo_id
            ?? record.s3Key
            ?? record.s3_key
            ?? record.cloudObjectKey
            ?? record.cloud_object_key
            ?? record.fileMetadataId
            ?? record.file_metadata_id
            ?? record.index;
        const fullerPhoto = matchingPhoto(reference, photos);
        if (fullerPhoto) {
            return toRenderablePhotoRecord(mergeHeroCandidateWithPhoto(record, fullerPhoto));
        }
        const renderable = toRenderablePhotoRecord(record);
        return hasResolvablePhotoSource(renderable) ? renderable : null;
    }

    const matched = matchingPhoto(value, photos);
    if (matched) return toRenderablePhotoRecord(matched);
    if (typeof value !== 'string') return null;

    const direct = value.trim();
    if (/^data:image\//i.test(direct)) return { data: direct, url: direct };
    // A persisted blob URL belongs to an earlier browser session and must not
    // be painted. Durable photo/file/S3 references above are the recovery path.
    if (/^blob:/i.test(direct)) return null;
    if (/^(?:https?:\/\/|\/)/i.test(direct)) return { url: direct };
    return null;
}

/**
 * Resolve the Run Sheet's saved hero selection to the same durable photo
 * record used by the rest of QuoteMaker. The Stage 5 header owns the final
 * quote presentation; the top-level selection is its derived cache/fallback.
 */
export function resolveRunSheetHeroCandidate({
    selectedHeroImage,
    quoteHeroImage,
    photos = [],
}: HeroCandidateInput): PhotoRecord | null {
    const photoRows = Array.isArray(photos)
        ? photos.filter((photo): photo is PhotoRecord => !!photo && typeof photo === 'object')
        : [];
    const selected = normalizeHeroCandidate(selectedHeroImage, photoRows);
    const quoteHeader = normalizeHeroCandidate(quoteHeroImage, photoRows);

    // A blobId is meaningful only in the browser profile that created it. Old
    // cloud projections can also retain preview/signed URLs after those URLs
    // expire. The Stage 5 header remains authoritative only when it names a
    // durable cloud object. Otherwise a durable selected cache wins, matching
    // usePhotoSrc's durable-before-session fallback order. Keep session/local
    // candidates only when no durable candidate exists so legacy/offline
    // projects retain their current behaviour.
    if (quoteHeader && hasDurablePhotoIdentity(quoteHeader)) return quoteHeader;
    if (selected && hasDurablePhotoIdentity(selected)) return selected;
    if (quoteHeader && hasPortablePhotoSource(quoteHeader)) return quoteHeader;
    if (selected && hasPortablePhotoSource(selected)) return selected;
    return quoteHeader || selected;
}

export default function RunSheetHeroThumbnail({
    candidate,
    projectId,
}: {
    candidate: unknown;
    projectId?: string | null;
}) {
    // Offline Dexie summaries may still carry the legacy string form. Resolve
    // it here as a final component boundary so no caller can accidentally feed
    // a raw object/id/data URL to the native img element again.
    // The shared hook tracks source identity as well as source fields. Keep
    // legacy string wrappers stable across the state update that publishes the
    // resolved src; recreating `{ data }` on every render makes that receipt
    // look as though it belongs to a different photo.
    const normalizedCandidate = useMemo(
        () => resolveRunSheetHeroCandidate({ selectedHeroImage: candidate }),
        [candidate],
    );
    const src = usePhotoSrc(normalizedCandidate, projectId || null);
    const [failedSrc, setFailedSrc] = useState<string | null>(null);

    if (!src || src === failedSrc) return null;
    return (
        <img
            className="rs-hero__thumb"
            src={src}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            onError={() => setFailedSrc(src)}
        />
    );
}
